import { daylightInfo, type DaylightInfo } from '../lib/sun';
import { fmtTime } from '../lib/time';
import {
  buildAdvice,
  describeWeatherCode,
  weatherAt,
  type PresetKey,
  type WeatherIcon,
} from '../lib/weather';
import { PRESET_SLIDER, useAppStore } from '../state/store';
import { useWeather } from '../state/useWeather';
import { IconCheck, WeatherGlyph } from './Icons';

function daylightText(d: DaylightInfo): string {
  if (d.kind === 'polarDay') return 'Giorno polare: il sole non tramonta';
  if (d.kind === 'polarNight') return 'Notte polare: il sole non sorge';
  if (d.isDay) {
    const parts: string[] = [];
    if (d.prevSunrise) parts.push(`alba ${fmtTime(d.prevSunrise)}`);
    if (d.nextSunset) parts.push(`tramonto ${fmtTime(d.nextSunset)}`);
    return parts.length ? parts.join(' · ') : 'Giorno';
  }
  return d.nextSunrise ? `Notte · alba alle ${fmtTime(d.nextSunrise)}` : 'Notte';
}

function glyphTone(icon: WeatherIcon): string {
  switch (icon) {
    case 'sun':
    case 'uv':
    case 'heat':
      return 'weather__glyph--sun';
    case 'moon':
    case 'sunset':
      return 'weather__glyph--night';
    case 'rain':
    case 'storm':
    case 'snow':
    case 'cold':
      return 'weather__glyph--rain';
    default:
      return '';
  }
}

/** Meteo e luce per il percorso: informa e propone, non decide. */
export function WeatherCard() {
  const plan = useAppStore((s) => s.plan);
  const planDepartMs = useAppStore((s) => s.planDepartMs);
  const mode = useAppStore((s) => s.mode);
  const sliderPct = useAppStore((s) => s.sliderPct);
  const applyPreset = useAppStore((s) => s.applyPreset);

  const mid = plan ? plan.chosen.coords[Math.floor(plan.chosen.coords.length / 2)] : null;
  const { forecast, status } = useWeather(mid);

  if (!plan || planDepartMs === null || !mid) return null;

  const arrivalMs = planDepartMs + plan.chosen.durationS * 1000;
  const daylight = daylightInfo(planDepartMs, mid.lat, mid.lng);
  const depart = weatherAt(forecast, planDepartMs);
  const arrive = weatherAt(forecast, arrivalMs);
  const chosenIsShortest = Math.abs(plan.chosen.lengthM - plan.shortest.lengthM) < 0.5;
  const advice = buildAdvice({
    depart,
    arrive,
    daylight,
    departMs: planDepartMs,
    arrivalMs,
    chosenShadePct: Math.round(plan.chosen.shadeFrac * 100),
    chosenIsShortest,
    fmtTime,
  });

  const desc = depart ? describeWeatherCode(depart.weatherCode) : null;
  const headIcon: WeatherIcon = desc
    ? depart && !depart.isDay && desc.icon === 'sun'
      ? 'moon'
      : desc.icon
    : daylight.isDay
      ? 'sun'
      : 'moon';
  const title =
    desc && depart
      ? `${desc.label} · ${Math.round(depart.tempC)}°`
      : status === 'loading'
        ? 'Carico il meteo…'
        : status === 'ok'
          ? 'Nessuna previsione per quest’ora'
          : 'Meteo non disponibile';

  const isActive = (preset: PresetKey) =>
    preset === 'auto' ? mode === 'auto' : mode === 'manual' && sliderPct === PRESET_SLIDER[preset];

  return (
    <section className="card weather" aria-label="Meteo e luce">
      <div className="weather__head">
        <span className={`weather__glyph ${glyphTone(headIcon)}`}>
          <WeatherGlyph icon={headIcon} size={26} />
        </span>
        <div className="grow">
          <div className="t-headline">{title}</div>
          <div className="t-sub">{daylightText(daylight)}</div>
        </div>
      </div>

      {depart && (
        <div className="weather__facts">
          <span
            className={`fact${depart.uvIndex >= 8 ? ' fact--warn' : depart.uvIndex >= 6 ? ' fact--tip' : ''}`}
          >
            UV {Math.round(depart.uvIndex)}
          </span>
          <span className="fact">Nuvole {Math.round(depart.cloudCoverPct)}%</span>
          {depart.precipProbPct >= 20 && (
            <span className={`fact${depart.precipProbPct >= 60 ? ' fact--tip' : ''}`}>
              Pioggia {Math.round(depart.precipProbPct)}%
            </span>
          )}
          {Math.abs(depart.apparentC - depart.tempC) >= 2 && (
            <span className="fact">Percepiti {Math.round(depart.apparentC)}°</span>
          )}
          {arrive && arrive !== depart && arrive.weatherCode !== depart.weatherCode && (
            <span className="fact">
              All’arrivo: {describeWeatherCode(arrive.weatherCode).label}
            </span>
          )}
        </div>
      )}

      {status === 'error' && (
        <p className="t-cap weather__offline">
          Previsioni non raggiungibili: il calcolo dell’ombra non ne dipende.
        </p>
      )}

      {advice.map((a, i) => (
        <div key={i} className={`advice advice--${a.level}`}>
          <span className="advice__icon">
            <WeatherGlyph icon={a.icon} size={18} />
          </span>
          <span className="grow">{a.text}</span>
          {a.action &&
            (isActive(a.action.preset) ? (
              <span className="advice__done">
                <IconCheck size={13} /> attivo
              </span>
            ) : (
              <button
                type="button"
                className="btn btn--xs btn--tinted advice__action"
                onClick={() => applyPreset(a.action!.preset)}
              >
                {a.action.label}
              </button>
            ))}
        </div>
      ))}
    </section>
  );
}
